import { useIntl } from 'react-intl';
import { HStack } from '@astryxdesign/core/HStack';
import { MultiSelector } from '@astryxdesign/core/MultiSelector';
import { Selector } from '@astryxdesign/core/Selector';
import { TextInput } from '@astryxdesign/core/TextInput';
import { NumberInput } from '@astryxdesign/core/NumberInput';
import { DateInput } from '@astryxdesign/core/DateInput';
import { DateRangeInput } from '@astryxdesign/core/DateRangeInput';
import type { DateRange } from '@astryxdesign/core/DateRangeInput';
import type { DimensionMeta, FilterCondition } from '@qronstat/shared';
import { operatorSpec } from '@qronstat/ui/filters';
import { useDimensionValues } from './useDimensionValues';
import type { IsoDate } from '../../model/dates';
import { useSelectedText } from '../useSelectedText';

type Values = NonNullable<FilterCondition['values']>;

export function ValueInput({
  dim,
  condition,
  onChange,
}: {
  dim: DimensionMeta;
  condition: FilterCondition;
  onChange: (values: Values) => void;
}) {
  const intl = useIntl();
  const selectedText = useSelectedText();
  const values = condition.values ?? [];
  const spec = operatorSpec(condition.operator);
  const multi = spec.shape === 'list';
  const pick = dim.type === 'string' && spec.pickFromValues;
  const { options, loading } = useDimensionValues(dim, pick);
  const label = intl.formatMessage({ id: 'filters.value' });
  if (spec.shape === 'none') return null;
  if (pick) {
    if (multi) {
      return (
        <MultiSelector
          formatValue={selectedText}
          label={label}
          isLabelHidden
          size="sm"
          hasSearch
          triggerDisplay="labels"
          value={values.map(String)}
          onChange={(v: string[]) => onChange(v)}
          options={options}
          placeholder={
            loading
              ? intl.formatMessage({ id: 'common.loading' })
              : intl.formatMessage({ id: 'filters.pickValues' })
          }
          searchPlaceholder={intl.formatMessage({ id: 'common.searchPlaceholder' })}
          emptyText={intl.formatMessage({ id: 'common.nothingFound' })}
        />
      );
    }
    return (
      <Selector
        label={label}
        isLabelHidden
        size="sm"
        hasSearch
        value={values[0] === undefined ? undefined : String(values[0])}
        onChange={(v) => onChange(v ? [v] : [])}
        options={options}
        placeholder={
          loading
            ? intl.formatMessage({ id: 'common.loading' })
            : intl.formatMessage({ id: 'filters.pickValue' })
        }
        searchPlaceholder={intl.formatMessage({ id: 'common.searchPlaceholder' })}
      />
    );
  }
  if (dim.type === 'string') {
    return (
      <TextInput
        label={label}
        isLabelHidden
        size="sm"
        value={String(values[0] ?? '')}
        onChange={(v: string) => onChange(v ? [v] : [])}
      />
    );
  }
  if (dim.type === 'number') {
    if (spec.shape === 'pair') {
      return (
        <HStack gap={1} vAlign="center">
          <NumberInput
            label={intl.formatMessage({ id: 'filters.from' })}
            isLabelHidden
            size="sm"
            value={values[0] === undefined ? null : Number(values[0])}
            onChange={(v: number) => onChange([v, values[1] ?? v])}
          />
          <NumberInput
            label={intl.formatMessage({ id: 'filters.to' })}
            isLabelHidden
            size="sm"
            value={values[1] === undefined ? null : Number(values[1])}
            onChange={(v: number) => onChange([values[0] ?? v, v])}
          />
        </HStack>
      );
    }
    return (
      <NumberInput
        label={label}
        isLabelHidden
        size="sm"
        value={values[0] === undefined ? null : Number(values[0])}
        onChange={(v: number) => onChange([v])}
      />
    );
  }
  if (dim.type === 'time') {
    if (spec.shape === 'days') {
      return (
        <NumberInput
          label={intl.formatMessage({ id: 'filters.days' })}
          isLabelHidden
          size="sm"
          value={values[0] === undefined ? null : Number(values[0])}
          onChange={(v: number) => onChange([Math.max(1, Math.round(v))])}
        />
      );
    }
    if (spec.shape === 'pair') {
      const range: DateRange | null =
        values.length === 2
          ? { start: String(values[0]) as IsoDate, end: String(values[1]) as IsoDate }
          : null;
      return (
        <DateRangeInput
          label={label}
          isLabelHidden
          size="sm"
          weekStartsOn="mon"
          value={range}
          onChange={(r: DateRange | null) => onChange(r ? [r.start, r.end] : [])}
        />
      );
    }
    return (
      <DateInput
        label={label}
        isLabelHidden
        size="sm"
        weekStartsOn="mon"
        format="date"
        {...(values[0] ? { value: String(values[0]) as IsoDate } : {})}
        onChange={(v) => onChange(v ? [v] : [])}
      />
    );
  }
  return null;
}
